import { sound as PIXI_SOUND } from "@pixi/sound";
import * as PIXI from "pixi.js-legacy";
import createjs from "../createjs";
import AudioVolumes from "../AudioVolumes";
import AbstractAudio, { AUDIO_EVENTS } from "../AbstractAudio";
import { inject, injectable } from "inversify";
import events from "../../events/EventsObservable";
import SERVICE_IDENTIFIER from "../../constants/identifiers";
import SystemConfig from "../../system/SystemConfig";
import Settings from "../../settings/Settings";

@injectable()
export default class PixiAudio extends AbstractAudio {
	private _tracks: Record<string, any>;
	private _volumes: AudioVolumes;

	constructor(@inject(SERVICE_IDENTIFIER.SystemConfig) config: SystemConfig) {
		super(config);

		this._tracks = {};
		this._volumes = new AudioVolumes();
	}

	onInitSuccess(settings: Settings) {
		this._volumes.init(this.gamesetTracks, settings);
	}

	/**
	 *
	 * @param {PIXI.resources.Resource} data
	 */
	loadComplete(data) {
		if (!data) return;

		let tracks = this.gameset.game.audio.tracks.slice(0);
		if (this.gameset.system && this.gameset.system.audio) {
			tracks = tracks.concat(this.gameset.system.audio.tracks.slice(0));
		}

		for (let track of tracks) {
			if (typeof this[track.name] !== "undefined") {
				throw new Error("Implementig existing property on MadmaxAudio!!!");
			}

			if (track.reference) {
				let referenceTrack = this._getReferenceTrack(track.reference, tracks);
				if (!referenceTrack) {
					return;
				}

				this._tracks[track.name] = this._tracks[track.reference];
				if (track.loop !== undefined) {
					this._tracks[track.name].loop = !!track.loop;
				} else if (referenceTrack.loop !== undefined) {
					this._tracks[track.name].loop = !!tracks[track.reference].loop;
				}
			} else {
				this._tracks[track.name] = data[track.name].sound;
				if (track.loop !== undefined) {
					this._tracks[track.name].loop = !!track.loop;
				}
			}

			this._tracks[track.name].volume = this._volumes.get(track.name);

			//this[track.name] = this._tracks[track.name];
		}

		this.mute();
	}

	/**
	 *
	 * @param {string} name
	 * @param {Array<Object>} tracks
	 * @private
	 */
	_getReferenceTrack(name, tracks) {
		for (let track of tracks) {
			if (track.name === name) {
				return track;
			}
		}
	}

	/**
	 *
	 * @param {number} duration
	 * @param {?Function} easing
	 */
	audioFadeIn(duration, easing = undefined) {
		if (!super.unmuteAllowed()) {
			return;
		}

		createjs.Tween.get(PIXI_SOUND, { override: true }).to(
			{ volumeAll: 1 },
			duration,
			easing
		);
	}

	mute() {
		this.muted = true;

		createjs.Tween.removeTweens(PIXI_SOUND);
		PIXI_SOUND.volumeAll = 0;
		events.dispatch(AUDIO_EVENTS.AUDIO_MUTED, true);
		return true;
	}

	unmute() {
		if (!this.choosed || !this.unmuteAllowed()) {
			return false;
		}

		this.muted = false;
		PIXI_SOUND.volumeAll = 0;
		this.audioFadeIn(500);
		events.dispatch(AUDIO_EVENTS.AUDIO_MUTED, false);
		return true;
	}

	getTrack(name) {
		return this._tracks[name];
	}

	/**
	 *
	 * @param {string} name
	 * @return {boolean}
	 */
	trackExists(name) {
		return !!this.getTrack(name);
	}

	/**
	 *
	 * @param {string} name
	 * @param {number|null} vol
	 * @return {PIXI_SOUND}
	 */
	playTrack(name, vol = null) {
		try {
			this._tracks[name].volume = this.getDefaultVolume(name, vol);
			this._tracks[name].play();
		} catch (e) {
			console.error(name, e, this._tracks[name]);
		}
		return this._tracks[name];
	}

	/**
	 *
	 * @param {string} name
	 * @return {PIXI_SOUND}
	 */
	stopTrack(name) {
		this._tracks[name].stop();
		return this._tracks[name];
	}

	/**
	 *
	 * @param {string} name
	 * @param {number} duration
	 * @param {number} from
	 * @param {?Function} callback
	 * @param {?Function} easing
	 */
	fadeInTrack(
		name,
		duration,
		from = undefined,
		callback = undefined,
		easing = undefined
	) {
		if (from !== undefined && from >= 0) {
			this._tracks[name].volume = from;
		}

		createjs.Tween.get(this._tracks[name], { override: true })
			.to({ volume: this._volumes.get(name) }, duration)
			.call(() => {
				if (typeof callback === "function") {
					callback();
				}
			});
	}

	/**
	 *
	 * @param {string} name
	 * @param {number} duration
	 * @param {?Function} callback
	 * @param {?Function} easing
	 */
	fadeInPlaying(name, duration, callback = undefined, easing = undefined) {
		let vol = 0;
		if (this.isPlaying(name)) {
			vol = this.getVolume(name);
		} else {
			this.playTrack(name, vol);
		}

		this.fadeInTrack(name, duration, vol, callback);
	}

	/**
	 *
	 * @param {string} name
	 * @param {number} duration
	 * @param {boolean} stop
	 * @param {?Function} callback
	 * @param {?Function} easing
	 */
	fadeOutTrack(
		name,
		duration,
		stop = true,
		callback = undefined,
		easing = createjs.Ease.quadInOut
	) {
		createjs.Tween.get(this._tracks[name], { override: true })
			.to({ volume: 0 }, duration, easing)
			.call(() => {
				if (stop) this.stopTrack(name);
				callback && callback();
			});
	}

	/**
	 *
	 * @param {string} name
	 * @param {number} toPerc
	 * @param {number} duration
	 * @param {?Function} callback
	 * @param {?Function} easing
	 */
	fadeToTrack(name, toPerc, duration, callback = () => {}, easing = undefined) {
		if (this._lockVolumes) {
			return;
		}
		let vol = this._volumes.get(name) * toPerc;

		createjs.Tween.get(this._tracks[name], { override: true })
			.to({ volume: vol }, duration)
			.call(callback);
	}

	/**
	 * @param {string} name
	 * @return {number}
	 */
	getVolume(name) {
		return this._tracks[name].volume;
	}

	getDefaultVolume(name, vol = null) {
		if (vol != null) {
			return vol;
		}

		return this._volumes.get(name);
	}

	/**
	 * @param {string} name
	 * @param {number} vol
	 */
	setVolume(name, vol) {
		this._tracks[name].volume = vol;
	}

	/**
	 * @param {string} name
	 * @return {boolean}
	 */
	isPlaying(name) {
		try {
			return this._tracks[name]?.isPlaying;
		} catch (e) {
			console.error(name, e, this._tracks[name]);
		}
	}

	getTrackNames() {
		return Object.keys(this._tracks);
	}
}
